'use strict';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import {expiresIn} from '../../auth/utils/token';
import {getApiToken} from '../../auth/reducers';
import {isWebView} from "../../core-utilities-implementation/device-services/DeviceServicesCordova";

FetchUtils.$inject = [
  '$ngRedux',
  '$toast',
  'FirebaseUtil',
];

export function FetchUtils(
  $ngRedux,
  $toast,
  FirebaseUtil,
) {
  this.$ngRedux = $ngRedux;
  this.$toast = $toast;
  this.FirebaseUtil = FirebaseUtil;
  this.API_URL = process.env.API_URL;
}

FetchUtils.prototype.request = function requestWithAuth(url, request) {
  if (!isObject(request.headers)) {
    request.headers = {};
  }
  request.headers["Accept"] = "application/json";

  if (isWebView()) {
    return this.rawRequest(url, request)
      .then((response) => {
        if (response.ok) {
          return response.response;
        } else {
          return Promise.reject(response.response);
        }
      })
  }

  return this.rawRequest(url, request)
    .then(simpleResponse);
};

FetchUtils.prototype.requestWithAuth = function requestWithAuth(url, request) {
  if (!this.hasValidToken())
    return Promise.reject(new Error('Authentication Expired'));

  const token = getApiToken(this.$ngRedux.getState());
  if (!isObject(request.headers)) {
    request.headers = {};
  }
  request.headers["Accept"] = "application/json";
  request.headers["Authorization"] = "Bearer " + token;
  request.headers["Cache-Control"] = "no-cache"; // may need to disable this in production

  if (isWebView()) {
    return this.rawRequest(url, request)
      .then((response) => {
        if (response.ok) {
          return response.response;
        } else {
          return Promise.reject(response.response);
        }
      })
  }

  return this.rawRequest(url, request)
    .then(simpleResponse);
};

FetchUtils.prototype.rawRequest = function rawRequest(url, request) {
  if (isWebView()) {
    const config = {
      method: request.method.toUpperCase(),
      responseType: 'json',
      headers: request.headers || {},
    }
    handleCordovaBody(request, config);
    if ((request.method === 'POST' || request.method === 'PATCH') && !request.body) {
      config.data = {};
    }
    return sendCordovaRequest(url, config, request)
  }

  handleFetchBody(request);
  return Promise.resolve(fetch(url, request))
    .then(parseResponse);
};

function parseResponse(response) {
  const contentType = response.headers.get("content-type");
  const getData = contentType && contentType.indexOf("application/json") !== -1 ? response.json() : response.text()
  return getData.then((data) => ({
    ok: response.ok,
    data,
    status: response.status,
    statusText: response.statusText,
    // TODO
    responseType: contentType || 'json',
  }))
}

function simpleResponse(response) {
  if (response.ok) {
    return Promise.resolve(response.data);
  } else {
    if (response.responseType && response.responseType.indexOf("json") !== -1) {
      return Promise.reject(response.data);
    } else {
      return Promise.reject(new Error(
        response.statusText || (response.data && response.data.message) || 'Unexpected error occurred'
      ));
    }
  }
}

FetchUtils.prototype.rawRequestWithAuth = function requestWithAuth(url, request) {
  if (!this.hasValidToken())
    return Promise.reject(new Error('Authentication Expired'));

  const token = getApiToken(this.$ngRedux.getState());
  if (!isObject(request.headers)) {
    request.headers = {};
  }
  request.headers["Accept"] = "application/json";
  request.headers["Authorization"] = "Bearer " + token;
  request.headers["Cache-Control"] = "no-cache"; // may need to disable this in production

  if (isWebView()) {
    return this.rawRequest(url, request)
      .then((response) => {
        if (response.ok) {
          return response.response;
        } else {
          return Promise.reject(response.response);
        }
      });
  }

  return this.rawRequest(url, request);
};

FetchUtils.getResponse = FetchUtils.prototype.getResponse = function getResponse(response) {
  const contentType = response.headers.get("content-type");
  if (!response.ok) {
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return response.json().then((json) => {
        return Promise.reject(json);
      });
    } else {
      return Promise.reject(new Error(response.statusText));
    }
  } else {
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return response.json();
    } else {
      return response.text();
    }
  }
};

FetchUtils.prototype.hasValidToken = function hasValidToken() {
  const token = getApiToken(this.$ngRedux.getState());
  return token && expiresIn(token) > 0; // is not expired
};

FetchUtils.prototype.getHeaders = function getHeaders() {
  const token = getApiToken(this.$ngRedux.getState());
  return {
    Accept: 'application/json',
    'Cache-Control': 'no-cache', // may need to disable this in production
    'Authorization': 'Bearer ' + token,
  };
};

FetchUtils.prototype.notificationErrorHandler = function (error) {
  if (error.toString() === 'TypeError: Failed to fetch') {
    this.$toast.showI18nMessage({messageId: 'default.error.offline.message', type: this.$toast.TOAST_TYPES.error});
  } else if (isObject(error) && isString(error.code)) {
    this.$toast.showI18nMessage({messageId: error.code, type: this.$toast.TOAST_TYPES.error});
  } else {
    this.$toast.showI18nMessage({messageId: 'default.error.message', type: this.$toast.TOAST_TYPES.error});
  }
};

FetchUtils.prototype.logFallbackUsage = function (data) {
  // const token = getApiToken(this.$ngRedux.getState());
  // const FirebaseUtil = this.FirebaseUtil;
  // return function (response) {
  //   const tokens = token ? token.split('.') : [];
  //   FirebaseUtil.firebaseRef('_debug_data_').push(JSON.stringify({
  //     date: new Date(),
  //     data: data,
  //     token: tokens.length > 2 ? tokens[1] : '',
  //   }));
  //   return response;
  // };
  return function (response) {
    return response;
  };
};

function handleFetchBody(request) {
  if (request.body) {
    switch (request.contentType) {
      case 'json':
        request.headers['Content-Type'] = 'application/json';
        request.body = JSON.stringify(request.body)
        break
      default:
        console.warn(`Unhandled fetchUtils.handleFetchBody case ${request.contentType}`)
      // TODO
    }
  }
}

function handleCordovaBody(request, config) {
  if (request.body) {
    switch (request.contentType) {
      case 'json':
        config.headers['Content-Type'] = 'application/json';
        config.serializer = 'json'
        config.data = request.body
        break
      default:
        console.warn(`Unhandled fetchUtils.handleCordovaBody case ${request.contentType}`)
      // TODO
    }
  }
}

function sendCordovaRequest(url, config, request) {
  return new Promise((resolve, reject) => {
    const msg = `[CordovaHTTP][FetchUtils] ${request.method.toUpperCase()} ${url}`;
    try {
      window.cordova.plugin.http.sendRequest(
        url,
        config,
        response => {
          console.log(`${msg} response`, {
            url,
            config,
            response,
            request,
          });
          resolve({
            ok: true,
            data: response.data,
            status: response.status,
            responseType: request.responseType || 'json',
          });
        },
        response => {
          if (response.status >= 300 && response.status < 400) {
            console.log(`${msg} response`, {
              url,
              config,
              response,
              request,
            });
            resolve({
              ok: true,
              data: parseCordovaError(response.error),
              status: response.status,
              responseType: request.responseType || 'json',
            });
            return
          }
          console.error(`${msg} error`, {
            url,
            config,
            error: response,
            request,
          });
          resolve({
            ok: false,
            data: parseCordovaError(response.error),
            status: response.status,
            responseType: request.responseType || 'json',
          });
        },
      );
    } catch (error) {
      console.error(`${msg} threw Exception`, {
        url,
        config,
        error,
        request,
      });
      reject(error)
    }
  });
}

function parseCordovaError(error) {
  const type = typeof error
  switch (type) {
    case 'string':
      try {
        return JSON.parse(error);
      } catch (e) {
        return { message: error };
      }
    case 'object':
      return error ? error : { message: 'Unexpected error occurred' };
    default:
      console.warn(`Unsupported error type from cordova.plugin.http.sendRequest "${type}"`, error)
      return {
        message: 'Unexpected error occurred',
      }
  }
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/fetch-utils/fetchUtils.js